import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CallCenterModal from "../../components/call-center/listing/CallCenterModal";
import { FiCalendar, FiEdit2, FiEye, FiPlus, FiSearch, FiXCircle } from "react-icons/fi";
import useCallCenterT from "../../hooks/useCallCenterT";
import AccountPlanFormModal, {
  buildAccountPlanPayload,
} from "../../components/call-center/AccountPlanFormModal";
import { hasScreenAccess } from "../../utils/permissions";
import {
  cancelAccountPlan,
  createAccountPlan,
  getAccountPlan,
  getAccountPlanMetadata,
  listAccountPlans,
  updateAccountPlan,
} from "../../service/CallCenterAccountPlans";
import ConfirmDialog from "../../components/call-center/listing/ConfirmDialog";
import TableSkeleton from "../../components/call-center/listing/TableSkeleton";
import ListingEmptyState from "../../components/call-center/listing/ListingEmptyState";
import ListingPaginationFooter from "../../components/call-center/listing/ListingPaginationFooter";
import ListingFilterHeader from "../../components/call-center/listing/ListingFilterHeader";
import StatusBadge from "../../components/call-center/listing/StatusBadge";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_CARD_CLASS,
  LISTING_CONTAINER_CLASS,
  LISTING_FILTER_CARD_CLASS,
  LISTING_ICON_ACTION_CLASS,
  LISTING_ICON_ACTION_DANGER_CLASS,
  LISTING_PAGE_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
  LISTING_TABLE_HEAD_CLASS,
  LISTING_TABLE_HEADER_CELL_CLASS,
  LISTING_TABLE_ROW_CLASS,
} from "../../components/call-center/listing/listingStyles";

const PAGE_SIZE = 20;

const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return `${amount.toFixed(3)} OMR`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const resolvePlanTotal = (plan) => {
  if (plan?.totalAmount != null) return Number(plan.totalAmount);
  const per = Number(plan?.installmentAmount);
  const count = Number(plan?.installmentCount);
  if (Number.isFinite(per) && Number.isFinite(count)) return per * count;
  return null;
};

const ProgressBar = ({ completed, total }) => {
  const safeTotal = Math.max(Number(total) || 0, 1);
  const safeCompleted = Math.min(Math.max(Number(completed) || 0, 0), safeTotal);
  const percent = Math.round((safeCompleted / safeTotal) * 100);
  return (
    <div className="min-w-[120px]">
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-medium text-slate-700">
          {safeCompleted}/{safeTotal}
        </span>
        <span className="text-slate-400">{percent}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-[#2B7FFF] transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

const CallCenterAccountPlansPage = () => {
  const t = useCallCenterT();
  const canCreate = hasScreenAccess("CC_ACCOUNT_PLAN", "canCreate");
  const canUpdate = hasScreenAccess("CC_ACCOUNT_PLAN", "canUpdate");

  const [plans, setPlans] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [assignedToFilter, setAssignedToFilter] = useState("");
  const [accountSearchInput, setAccountSearchInput] = useState("");
  const [accountSearch, setAccountSearch] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detailPlan, setDetailPlan] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const frequencyLabel = useCallback(
    (value) => {
      const match = metadata?.frequencies?.find((item) => item.code === value);
      return match?.label || t(`ccAccountPlanFrequency_${value}`, value);
    },
    [metadata, t]
  );

  const statusLabel = useCallback(
    (value) => t(`ccAccountPlanStatus_${value}`, value),
    [t]
  );

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await listAccountPlans({
        page,
        size: PAGE_SIZE,
        status: statusFilter || undefined,
        accountNo: accountSearch || undefined,
        assignedTo: assignedToFilter || undefined,
      });
      setPlans(response?.items || []);
      setTotalPages(response?.totalPages || 0);
      setTotalElements(response?.totalElements || 0);
    } catch (err) {
      setError(err?.message || t("ccAccountPlansLoadFailed"));
      setPlans([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, accountSearch, assignedToFilter, t]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    getAccountPlanMetadata()
      .then(setMetadata)
      .catch(() => setMetadata(null));
  }, []);

  const applyFilters = () => {
    setPage(0);
    setAccountSearch(accountSearchInput.trim());
  };

  const resetFilters = () => {
    setAccountSearchInput("");
    setAccountSearch("");
    setStatusFilter("");
    setAssignedToFilter("");
    setPage(0);
  };

  const openCreateForm = () => {
    setEditingPlan(null);
    setFormOpen(true);
  };

  const openEditForm = (plan) => {
    setEditingPlan(plan);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingPlan(null);
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    setError("");
    try {
      const payload = buildAccountPlanPayload(values);
      if (editingPlan) {
        await updateAccountPlan(editingPlan.id, {
          mobileNumbers: payload.mobileNumbers,
          assignmentMode: payload.assignmentMode,
          assignedTo: payload.assignedTo,
          reminderDaysBefore: payload.reminderDaysBefore,
          ticketPriority: payload.ticketPriority,
          remarksTemplate: payload.remarksTemplate,
        });
      } else {
        await createAccountPlan(payload);
      }
      closeForm();
      await loadPlans();
    } catch (err) {
      setError(err?.message || t("ccAccountPlansSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (planId) => {
    setDetailLoading(true);
    setDetailPlan(null);
    try {
      const plan = await getAccountPlan(planId);
      setDetailPlan(plan);
    } catch (err) {
      setError(err?.message || t("ccAccountPlansLoadFailed"));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailPlan(null);
    setDetailLoading(false);
  };

  const requestCancelPlan = (planId) => {
    if (!planId) return;
    setCancelTargetId(planId);
  };

  const closeCancelConfirm = () => {
    if (cancelling) return;
    setCancelTargetId(null);
  };

  const confirmCancelPlan = async () => {
    if (!cancelTargetId) return;
    setError("");
    setCancelling(true);
    try {
      await cancelAccountPlan(cancelTargetId);
      if (detailPlan?.id === cancelTargetId) {
        closeDetail();
      }
      setCancelTargetId(null);
      await loadPlans();
    } catch (err) {
      setError(err?.message || t("ccAccountPlanCancelFailed"));
    } finally {
      setCancelling(false);
    }
  };

  const reminderOptions = useMemo(
    () => metadata?.reminderOptions || [],
    [metadata]
  );

  const agentOptions = useMemo(
    () => metadata?.agents || [],
    [metadata]
  );

  const hasActiveFilters = Boolean(accountSearch || statusFilter || assignedToFilter);

  return (
    <div className={LISTING_PAGE_CLASS}>
      <div className={LISTING_CONTAINER_CLASS}>
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-[28px] font-bold tracking-tight text-[#111827]">
              {t("ccAccountPlansPageTitle")}
            </h1>
            <p className="mt-1 text-sm text-[#6b7280]">{t("ccAccountPlansPageSubtitle")}</p>
            {(canCreate || canUpdate) && (
              <Link
                to="/call-center/account-plan-types"
                className="mt-2 inline-block text-sm font-semibold text-[#2B7FFF] hover:underline"
              >
                {t("ccAccountPlansManageTypes")}
              </Link>
            )}
          </div>
          {canCreate && (
            <button
              type="button"
              onClick={openCreateForm}
              className={LISTING_PRIMARY_BUTTON_CLASS}
            >
              <FiPlus className="h-4 w-4" />
              {t("ccAccountPlanCreate")}
            </button>
          )}
        </header>

        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <section className={LISTING_FILTER_CARD_CLASS}>
          <ListingFilterHeader
            title={t("ccListFiltersTitle")}
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            resultsLabel={t("ccAccountPlanResultsLabel")}
            showingPageLabel={t("ccShowingPage")}
            ofLabel={t("ccOf")}
            refreshLabel={t("ccRefresh")}
            loading={loading}
            onRefresh={loadPlans}
          />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-12">
            <div className="xl:col-span-4">
              <label className={FILTER_LABEL_CLASS}>{t("ccAccountPlanAccount")}</label>
              <div className="flex gap-2">
                <input
                  value={accountSearchInput}
                  onChange={(event) => setAccountSearchInput(event.target.value)}
                  onKeyDown={(event) => event.key === "Enter" && applyFilters()}
                  placeholder={t("ccSearchAccountPlaceholder")}
                  className={FILTER_FIELD_CLASS}
                />
                <button
                  type="button"
                  onClick={applyFilters}
                  className="inline-flex h-10 shrink-0 items-center gap-1 rounded-xl bg-[#2B7FFF] px-3 text-sm font-semibold text-white hover:bg-[#1d6ef2]"
                >
                  <FiSearch className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="xl:col-span-3">
              <label className={FILTER_LABEL_CLASS}>{t("ccAccountPlanFilterStatus")}</label>
              <select
                value={statusFilter}
                onChange={(event) => {
                  setPage(0);
                  setStatusFilter(event.target.value);
                }}
                className={FILTER_FIELD_CLASS}
              >
                <option value="">{t("ccAccountPlanFilterAll")}</option>
                <option value="ACTIVE">{statusLabel("ACTIVE")}</option>
                <option value="COMPLETED">{statusLabel("COMPLETED")}</option>
                <option value="CANCELLED">{statusLabel("CANCELLED")}</option>
              </select>
            </div>

            <div className="xl:col-span-3">
              <label className={FILTER_LABEL_CLASS}>{t("ccAccountPlanAssignedAgent")}</label>
              <select
                value={assignedToFilter}
                onChange={(event) => {
                  setPage(0);
                  setAssignedToFilter(event.target.value);
                }}
                className={FILTER_FIELD_CLASS}
              >
                <option value="">{t("ccAllAssignees")}</option>
                {agentOptions.map((agent) => (
                  <option key={agent.userId} value={String(agent.userId)}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end xl:col-span-2">
              <button
                type="button"
                onClick={resetFilters}
                disabled={!hasActiveFilters}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("ccClearFilters")}
              </button>
            </div>
          </div>
        </section>

        <section className={LISTING_CARD_CLASS}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className={LISTING_TABLE_HEAD_CLASS}>
                <tr>
                  <th className={LISTING_TABLE_HEADER_CELL_CLASS}>
                    {t("ccAccountPlanAccount")}
                  </th>
                  <th className={LISTING_TABLE_HEADER_CELL_CLASS}>
                    {t("ccAccountPlanTotalAmount")}
                  </th>
                  <th className={LISTING_TABLE_HEADER_CELL_CLASS}>
                    {t("ccAccountPlanFrequency")}
                  </th>
                  <th className={LISTING_TABLE_HEADER_CELL_CLASS}>
                    {t("ccAccountPlanProgress")}
                  </th>
                  <th className={LISTING_TABLE_HEADER_CELL_CLASS}>
                    {t("ccAccountPlanNextDue")}
                  </th>
                  <th className={LISTING_TABLE_HEADER_CELL_CLASS}>
                    {t("ccAccountPlanAssignedAgent")}
                  </th>
                  <th className={LISTING_TABLE_HEADER_CELL_CLASS}>
                    {t("ccAccountPlanStatus")}
                  </th>
                  <th className={`${LISTING_TABLE_HEADER_CELL_CLASS} text-right`}>
                    {t("ccAccountPlanActions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton />
                ) : plans.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <ListingEmptyState
                        icon={FiCalendar}
                        title={t("ccAccountPlansEmptyTitle")}
                        hint={t("ccAccountPlansEmptyHint")}
                        action={
                          canCreate && !hasActiveFilters ? (
                            <button type="button" onClick={openCreateForm} className={`mt-5 ${LISTING_PRIMARY_BUTTON_CLASS}`}>
                              <FiPlus className="h-4 w-4" />
                              {t("ccAccountPlanCreate")}
                            </button>
                          ) : null
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  plans.map((plan) => (
                    <tr
                      key={plan.id}
                      className={LISTING_TABLE_ROW_CLASS}
                    >
                      <td className="px-4 py-4">
                        <div className="font-semibold text-slate-900">{plan.accountNo}</div>
                        {plan.planTypeName ? (
                          <div className="mt-0.5 text-xs text-[#2B7FFF]">{plan.planTypeName}</div>
                        ) : null}
                        <div className="mt-0.5 text-xs text-slate-400">
                          {plan.installmentCount} {t("ccAccountPlanInstallmentsLabel")}
                        </div>
                      </td>
                      <td className="px-4 py-4 font-medium text-slate-800">
                        {formatAmount(resolvePlanTotal(plan))}
                      </td>
                      <td className="px-4 py-4 text-slate-700">{frequencyLabel(plan.frequency)}</td>
                      <td className="px-4 py-4">
                        <ProgressBar
                          completed={plan.completedInstallments}
                          total={plan.installmentCount}
                        />
                      </td>
                      <td className="px-4 py-4 text-slate-700">{formatDate(plan.nextDueDate)}</td>
                      <td className="px-4 py-4 text-slate-700">
                        {plan.assignedToName || t("ccAccountPlanAutoAssign")}
                      </td>
                      <td className="px-4 py-4">
                        <StatusBadge status={plan.status} label={statusLabel(plan.status)} />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openDetail(plan.id)}
                            title={t("ccAccountPlanView")}
                            className={LISTING_ICON_ACTION_CLASS}
                          >
                            <FiEye className="h-4 w-4" />
                          </button>
                          {canUpdate && plan.status === "ACTIVE" && (
                            <>
                              <button
                                type="button"
                                onClick={() => openEditForm(plan)}
                                title={t("ccAccountPlanEdit")}
                                className={LISTING_ICON_ACTION_CLASS}
                              >
                                <FiEdit2 className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => requestCancelPlan(plan.id)}
                                title={t("ccAccountPlanCancel")}
                                className={LISTING_ICON_ACTION_DANGER_CLASS}
                              >
                                <FiXCircle className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <ListingPaginationFooter
            totalElements={totalElements}
            resultsLabel={t("ccAccountPlanResultsLabel")}
            page={page}
            totalPages={totalPages}
            loading={loading}
            onPrevious={() => setPage((value) => Math.max(0, value - 1))}
            onNext={() => setPage((value) => value + 1)}
            previousLabel={t("ccPrevious")}
            nextLabel={t("ccNext")}
          />
        </section>
      </div>

      <AccountPlanFormModal
        open={formOpen}
        editingPlan={editingPlan}
        metadata={metadata}
        saving={saving}
        reminderOptions={reminderOptions}
        agentOptions={agentOptions}
        onClose={closeForm}
        onSubmit={handleSubmit}
        t={t}
      />

      <ConfirmDialog
        open={Boolean(cancelTargetId)}
        title={t("ccConfirmDialogTitle")}
        message={t("ccAccountPlanCancelConfirm")}
        cancelLabel={t("ccCancel")}
        confirmLabel={t("ccConfirmDialogProceed")}
        processingLabel={t("ccProcessing")}
        variant="danger"
        loading={cancelling}
        onConfirm={confirmCancelPlan}
        onClose={closeCancelConfirm}
        overlayClassName="z-[250]"
      />

      <CallCenterModal
        open={Boolean(detailPlan || detailLoading)}
        onClose={closeDetail}
        title={t("ccAccountPlanDetailTitle")}
        subtitle={
          detailPlan ? `${detailPlan.accountNo} · ${statusLabel(detailPlan.status)}` : undefined
        }
        maxWidth="3xl"
        maxHeight="90vh"
        closeLabel={t("ccClose")}
        contentVariant="none"
        bodyClassName="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6"
      >
                  {detailLoading ? (
                    <div className="space-y-3">
                      {Array.from({ length: 4 }).map((_, index) => (
                        <div key={index} className="h-16 animate-pulse rounded-xl bg-slate-100" />
                      ))}
                    </div>
                  ) : detailPlan ? (
                    <>
                      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {[
                          [t("ccAccountPlanTotalAmount"), formatAmount(resolvePlanTotal(detailPlan))],
                          [t("ccAccountPlanFrequency"), frequencyLabel(detailPlan.frequency)],
                          [t("ccAccountPlanProgress"), `${detailPlan.completedInstallments}/${detailPlan.installmentCount}`],
                          [t("ccAccountPlanAssignedAgent"), detailPlan.assignedToName || t("ccAccountPlanAutoAssign")],
                          [t("ccAccountPlanNextDue"), formatDate(detailPlan.nextDueDate)],
                          [t("ccAccountPlanStatus"), statusLabel(detailPlan.status)],
                        ].map(([label, value]) => (
                          <div
                            key={label}
                            className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
                          >
                            <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                              {label}
                            </div>
                            <div className="mt-1 text-sm font-semibold text-slate-800">{value}</div>
                          </div>
                        ))}
                      </div>

                      <div className="mb-3 flex items-center justify-between gap-3">
                        <h3 className="text-sm font-semibold text-slate-900">
                          {t("ccAccountPlanInstallmentSchedule")}
                        </h3>
                        {canUpdate && detailPlan.status === "ACTIVE" && (
                          <button
                            type="button"
                            onClick={() => {
                              closeDetail();
                              openEditForm(detailPlan);
                            }}
                            className="text-sm font-semibold text-[#2B7FFF] hover:text-[#1d4ed8]"
                          >
                            {t("ccAccountPlanEdit")}
                          </button>
                        )}
                      </div>

                      <div className="overflow-x-auto rounded-xl border border-slate-200">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="px-3 py-2">#</th>
                              <th className="px-3 py-2">{t("ccAccountPlanDueDate")}</th>
                              <th className="px-3 py-2">{t("ccAccountPlanReminderDate")}</th>
                              <th className="px-3 py-2">{t("ccAccountPlanAmountColumn")}</th>
                              <th className="px-3 py-2">{t("ccAccountPlanStatus")}</th>
                              <th className="px-3 py-2">{t("ccAccountPlanTicket")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detailPlan.installments || []).map((installment) => (
                              <tr key={installment.id} className="border-t border-slate-100">
                                <td className="px-3 py-2">{installment.installmentNumber}</td>
                                <td className="px-3 py-2">{formatDate(installment.dueDate)}</td>
                                <td className="px-3 py-2">{formatDate(installment.reminderDate)}</td>
                                <td className="px-3 py-2 font-medium">
                                  {formatAmount(installment.amount)}
                                </td>
                                <td className="px-3 py-2">
                                  <StatusBadge
                                    status={installment.status}
                                    label={statusLabel(installment.status)}
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  {installment.ticketId ? (
                                    <Link
                                      to={`/call-center/tickets?ticketId=${installment.ticketId}`}
                                      className="font-semibold text-[#2B7FFF] hover:text-[#1d4ed8]"
                                    >
                                      {t("ccAccountPlanOpenTicket")}
                                    </Link>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : null}
      </CallCenterModal>
    </div>
  );
};

export default CallCenterAccountPlansPage;
